/**
 * WebMCP tool: navigate_to_organization_tab
 *
 * Exposes a tool that lets AI agents navigate to any tab of the Organization
 * page (/organization/:tab) without visual scraping. This tool is the shared
 * navigation building block reused by every Organization-level agent flow.
 *
 * Register this composable once at the OrganizationPage level so the tool is
 * available whenever the user is anywhere in the Organization section.
 *
 * Usage (in OrganizationPage.vue — <script setup>):
 *   useNavigateToOrganizationTool({ proxy: instance.proxy });
 */

import { onMounted, onBeforeUnmount } from "vue";
import {
  registerTool,
  unregisterTool,
  waitForModelContext,
} from "../core/useWebMcpRegistry.js";
import {
  registerDebugHook,
  unregisterDebugHook,
} from "../core/useWebMcpDebug.js";
import {
  ORGANIZATION_ROUTE_NAME,
  ORGANIZATION_TABS,
  organizationTabSlug,
} from "./routeConstants.js";
import router from "@/router";

const TOOL_NAME = "navigate_to_organization_tab";

/**
 * Returns the set of valid lowercase tab names accepted by the tool,
 * derived from ORGANIZATION_TABS so the list never drifts out of sync.
 */
const validTabsLower = new Set(
  Object.values(ORGANIZATION_TABS).map((t) => t.toLowerCase())
);

/**
 * Resolves a user-supplied tab name to the canonical casing required by the
 * router (e.g. "responsibilities" → "Responsibilities").
 */
function resolveTabName(input) {
  if (typeof input !== "string") return null;
  const lower = input.trim().toLowerCase();
  return (
    Object.values(ORGANIZATION_TABS).find((t) => t.toLowerCase() === lower) ??
    null
  );
}

export function useNavigateToOrganizationTool({ proxy }) {
  let debugHandler = null;
  let cancelWait = null;

  const execute = async (input = {}) => {
    const canonicalTab = resolveTabName(input.tab);
    if (!canonicalTab) {
      return {
        success: false,
        error: `Unknown tab "${input.tab}". Valid tabs: ${Object.values(ORGANIZATION_TABS).join(", ")}.`,
      };
    }

const currentTab = (router.currentRoute?.params?.tab || "").toLowerCase();
    if (currentTab === organizationTabSlug(canonicalTab)) {
      return {
        success: true,
        message: `Already on Organization > ${canonicalTab}.`,
        tab: canonicalTab,
      };
    }

    try {
      await router
        .push({ name: ORGANIZATION_ROUTE_NAME, params: { tab: organizationTabSlug(canonicalTab) } })
        .catch(() => {});
      return {
        success: true,
        message: `Navigated to Organization > ${canonicalTab}.`,
        tab: canonicalTab,
      };
    } catch (error) {
      return {
        success: false,
        error: `Navigation to Organization > ${canonicalTab} failed.`,
      };
    }
  };

  const toolDefinition = {
    name: TOOL_NAME,
    description:
      "Navigate to a specific tab of the Organization page " +
      `(${Object.values(ORGANIZATION_TABS).join(", ")}). ` +
      "Call this before any tool that requires a specific Organization tab to be active.",
    inputSchema: {
      type: "object",
      properties: {
        tab: {
          type: "string",
          description: `Target Organization tab. One of: ${Object.values(ORGANIZATION_TABS).join(", ")}.`,
          enum: Object.values(ORGANIZATION_TABS),
        },
      },
      required: ["tab"],
      additionalProperties: false,
    },
    execute: async (input = {}) => execute(input),
    annotations: { readOnlyHint: false },
  };

  onMounted(() => {
    cancelWait = waitForModelContext(() => {
      registerTool(toolDefinition, {
        onWarning: (msg, err) => console.warn(msg, err),
      });
    });
    debugHandler = async (input) => execute(input);
    registerDebugHook(TOOL_NAME, debugHandler);
  });

  onBeforeUnmount(() => {
    cancelWait?.();
    unregisterTool(TOOL_NAME);
    unregisterDebugHook(TOOL_NAME, debugHandler);
    debugHandler = null;
  });

  return { execute };
}
